import Repository from '@/repositories';
const SuppliersRepository = Repository.get('suppliers');
const CategoriesRepository = Repository.get('categories');
const CategoryTypesRepository = Repository.get('categorytypes');
const AlertsRepository = Repository.get('alerts');
const SingleConfigsRepository = Repository.get('singleconfigs');
const ApplicationRepository = Repository.get('application');

export const namespaced = true;

function createCurrencyFormatter(locale, currency) {
  // Create number formatter without currency style
  const numberFormatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  });

  // Create currency formatter to get currency symbol
  const currencyFormatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'narrowSymbol',
  });

  return {
    format: (value) => {
      // Get the currency symbol
      const formatted = currencyFormatter.format(0);
      const symbol = formatted.replace(/[\d.,\s]/g, '');

      // Format the number and combine with symbol
      const formattedNumber = numberFormatter.format(value);

      // Check if symbol comes before or after (locale dependent)
      const symbolFirst = formatted.indexOf(symbol) === 0;

      return symbolFirst
        ? `${symbol}${formattedNumber}`
        : `${formattedNumber}${symbol}`;
    },
  };
}

//Accessed through $store.state.X
export const state = {
  suppliers: [],
  categories: [],
  categoryTypes: [],
  alerts: [],
  ordersConfig: { splitItemsByWorkOrder: false },
  organizationConfig: { currency: 'USD' },
  appInfos: null,
  currencyFormatter: createCurrencyFormatter('en', 'USD'),
};

//Accessed through $store.commit('cache/setX', newValue)
export const mutations = {
  mutateSuppliers(state, newValue) {
    if (newValue != null) {
      state.suppliers = newValue;
    }
  },
  mutateCategories(state, newValue) {
    if (newValue != null) {
      state.categories = newValue;
    }
  },
  mutateCategoryTypes(state, newValue) {
    if (newValue != null) {
      state.categoryTypes = newValue;
    }
  },
  mutateAlerts(state, newValue) {
    if (newValue != null) {
      state.alerts = newValue;
    }
  },
  mutateOrdersConfig(state, newValue) {
    if (newValue != null) {
      state.ordersConfig = newValue;
    }
  },
  mutateAppInfos(state, newValue) {
    if (newValue != null) {
      state.appInfos = newValue;
    }
  },
  mutateOrganizationConfig(state, { newValue, lang }) {
    if (newValue != null) {
      state.organizationConfig = newValue;

      state.currencyFormatter = createCurrencyFormatter(
        lang,
        state.organizationConfig.currency
      );
    }
  },
};

//Accessed through $store.getters['cache/getX']
export const getters = {
  getCategoriesByCategoryType: (state) => (idPublic) => {
    return state.categories.filter(
      (x) => x.categoryTypeIdPublic.toUpperCase() == idPublic.toUpperCase()
    );
  },
};

//Accessed through $store.dispatch('cache/fetchX')
export const actions = {
  fetchSuppliers({ commit }) {
    return SuppliersRepository.getAll()
      .then((response) => {
        let suppliers = response.data.data;
        commit('mutateSuppliers', suppliers);
        return suppliers;
      })
      .catch(() => {});
  },
  fetchCategories({ commit }) {
    return CategoriesRepository.getAll()
      .then((response) => {
        let categories = response.data.data;
        commit('mutateCategories', categories);
        return categories;
      })
      .catch(() => {});
  },
  fetchCategoryTypes({ commit }) {
    return CategoryTypesRepository.getAll()
      .then((response) => {
        let categoryTypes = response.data;
        commit('mutateCategoryTypes', categoryTypes);
        return categoryTypes;
      })
      .catch(() => {});
  },
  fetchAlerts({ commit }) {
    return AlertsRepository.customREST
      .getSideBarAlerts()
      .then((response) => {
        let alerts = response.data;
        commit('mutateAlerts', alerts);
        return alerts;
      })
      .catch(() => {});
  },
  fetchOrdersConfig({ dispatch }) {
    return SingleConfigsRepository.get('OrdersConfig')
      .then((response) => {
        let ordersConfig = response.data;
        dispatch('setOrdersConfig', ordersConfig);
        return ordersConfig;
      })
      .catch(() => {});
  },
  fetchOrganizationConfig({ dispatch }) {
    return SingleConfigsRepository.get('OrganizationConfig')
      .then((response) => {
        let organizationConfig = response.data;
        dispatch('setOrganizationConfig', organizationConfig);
        return organizationConfig;
      })
      .catch(() => {});
  },
  fetchAppInfos({ commit }) {
    return ApplicationRepository.getAll()
      .then((response) => {
        let appInfos = response.data;
        commit('mutateAppInfos', appInfos);
        return appInfos;
      })
      .catch(() => {});
  },
  setOrdersConfig({ commit }, payload) {
    commit('mutateOrdersConfig', payload);
  },
  setOrganizationConfig({ commit, rootState }, payload) {
    commit('mutateOrganizationConfig', {
      newValue: payload,
      lang: rootState.user.config.language,
    });
  },
};
